<template>
  <div class="rounded-2xl border border-gray-200 bg-white p-6 h-full">
    <div class="flex flex-col gap-2 mb-6">
      <div class="w-full">
        <h3 class="text-lg font-semibold text-gray-800">Cash / Momo / Wallet</h3>
        <p class="mt-1 text-gray-500 text-theme-sm">
          Methode utilisées pour les liens de paiements
        </p>
      </div>

      <div class="relative">
        <div
          class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
        >
          <button
            v-for="option in options"
            :key="option.value"
            @click="selectPeriod(option)"
            :class="[
              selectedPeriod === option.value
                ? 'shadow-theme-xs text-gray-900  bg-white'
                : 'text-gray-500',
              'px-3 py-2 font-medium rounded-md text-theme-sm hover:text-gray-900 hover:shadow-theme-xs',
            ]"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
    <div class="max-w-full overflow-auto custom-scrollbar">
      <div>
        <div>
          <div class="-ml-4 min-w-full pl-2 overflow-hidden">
            <VueApexCharts
              type="donut"
              height="310"
              :options="chartOptions"
              :series="series"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import VueApexCharts from 'vue3-apexcharts'
import { onMounted, ref, watch } from 'vue'
import Commons from '@/shared/utils/commons'

const props = defineProps({
  statsData: {
    type: Object,
    default: () => ({ cash: null, momo: null })
  }
})

const options = [
  {
    value: 'year',
    label: 'Année',
  },
  {
    value: 'month',
    label: 'Mois',
  },
  {
    value: 'week',
    label: 'Semaine',
  },
]

const selectedPeriod = ref('week')
const oldStats = ref(null)

const series = ref([0, 0, 0])
const extendedSeriesData = ref([{}, {}, {}])


const labels = ['Mobile Money', 'Espèces', 'Wallet']

const chartOptions = ref({
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'center',
  },
  colors: ['#FF7200', '#FFA200', '#10B981'],
  chart: {
    fontFamily: 'Outfit, sans-serif',
    type: 'donut',
    toolbar: {
      show: false,
    },
  },
  fill: {
    gradient: {
      enabled: true,
      opacityFrom: 0.55,
      opacityTo: 0,
    },
  },
  markers: {
    size: 0,
  },
  labels,
  dataLabels: {
    enabled: false,
  },

  tooltip: {
    y: {
      title: { formatter: () => '' },
      formatter: (val, { seriesIndex, dataPointIndex, w }) => {
        const data = extendedSeriesData.value[seriesIndex] || {}
        return `<div style="padding: 8px;">
        <b>${labels[seriesIndex]}</b>: ${val} essaie${val > 1 ? 's' : ''}<br/>
        <b>Total:</b> ${data.amount?.toLocaleString() || 0} FCFA<br/>
        ${data.successCount || 0} réussi${(data.successCount || 0) > 1 ? 's' : ''} (${data.successAmount?.toLocaleString() || 0} FCFA)
        </div>`
      },
    },
  },
})

function selectPeriod(option, initial) {
  if (!initial && selectedPeriod.value == option.value) return
  selectedPeriod.value = option.value
  oldStats.value = null
  series.value = [0, 0, 0]
}

function setStats(data) {
  console.log("🚀 ~ setStats ~ data:", data)
  if (Commons.deepEqual(oldStats.value, data)) return
  oldStats.value = data
  series.value = [data.mobileMoney.count, data.cash.count, data.wallet.count]
  extendedSeriesData.value = [data.mobileMoney, data.cash, data.wallet]
}

function updateStats() {
  if (!props.statsData?.cash) return
  const periodKey = selectedPeriod.value
  const sumCount = (arr) => arr.reduce((acc, curr) => acc + (curr.count || 0), 0)
  const sumAmount = (arr) => arr.reduce((acc, curr) => acc + (curr.amount || 0), 0)
  
  const getStatsForMethod = (methodData) => {
    const isArray = Array.isArray(methodData)
    const totalData = isArray ? (methodData[periodKey] || []) : (methodData?.total?.[periodKey] || [])
    const successData = isArray ? [] : (methodData?.success?.[periodKey] || [])
    
    return {
      count: sumCount(totalData),
      amount: sumAmount(totalData),
      successCount: sumCount(successData),
      successAmount: sumAmount(successData)
    }
  }

  const stats = {
    mobileMoney: getStatsForMethod(props.statsData.mobileMoney || props.statsData.momo),
    cash: getStatsForMethod(props.statsData.cash),
    wallet: getStatsForMethod(props.statsData.wallet)
  }
  setStats(stats)
}

watch([() => props.statsData, selectedPeriod], () => {
  updateStats()
}, { deep: true })

onMounted(() => {
  selectPeriod(options[2], true)
  updateStats()
})
</script>

<style scoped>
.donut-chart {
  width: 100%;
}
</style>
